import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { PackageCheck } from 'lucide-react';
import dashboardAPI from '../../services/dashboard';
import PendingReceiptsModal from './PendingReceiptsModal';

/**
 * #182201 — sếp: "hiển thị gọn lại thành 1 dòng bao nhiêu phiếu nhập hàng đang có chờ, nhấn vào
 * dòng chữ đó thì hiển thị danh sách nhập hàng đang chờ ... 1 dòng thôi, giống như dòng chi phí
 * NVL, nhấp vào là hiển thị bảng phiếu chi".
 *
 * Thay cho `ImportRequestStatsCard` (thẻ 5 dòng, #145125) vì hai lý do, không phải chỉ vì gọn:
 *  - ô cũ đếm `ImportRequest` = phiếu nhập THỦ CÔNG `NK-`, luồng gần như không ai dùng (điều tra
 *    #175223: 13 bản ghi toàn hệ thống, 0 bản chờ duyệt ⇒ ô luôn hiện 0/n/0);
 *  - thứ sếp cần là luồng đặt hàng NCC thật (`PurchaseOrder` → `GoodsReceipt`) — "mua hàng Quang
 *    Nhung hay Shopee nhưng hàng chưa đến, NV chưa xác nhận nhập kho".
 * Màn `/inventory/import-requests` vẫn còn nguyên trong menu nên không mất đường xem phiếu `NK-`.
 *
 * Khuôn trình bày lấy đúng của dòng "NVL" trên thẻ Tổng chi phí (`RevenueSection.js:208-224`):
 * `Button variant="link"` cỡ small, gạch chân, bấm ra Modal bảng chi tiết.
 */
const PendingReceiptsLine = ({ selectedBranch, reloadKey }) => {
  const [count, setCount] = useState(null);
  // #230341 — số đơn quá hạn 24h chờ NV xác nhận. Cùng endpoint, cùng ngưỡng do server quyết.
  const [overdue, setOverdue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showList, setShowList] = useState(false);

  const branchId = selectedBranch?.id;

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // limit: 1 — chỉ cần con số `counts.total` do server đếm, dùng CHUNG endpoint với modal nên
      // số ngoài dòng và số trong bảng không thể lệch nhau.
      const res = await dashboardAPI.getPendingReceipts({
        page: 1,
        limit: 1,
        ...(branchId ? { branch_id: branchId } : {}),
      });
      setCount(Number(res?.counts?.total || 0));
      setOverdue(Number(res?.counts?.overdue || 0));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Không tải được số đơn đang chờ nhập kho');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount, reloadKey]);

  return (
    <>
      <div className="small text-muted d-flex align-items-center" style={{ gap: 6 }}>
        <PackageCheck size={14} />
        {loading ? (
          <span><Spinner animation="border" size="sm" /> Đang đếm đơn chờ nhập kho...</span>
        ) : error ? (
          <span className="text-danger">
            {error}{' '}
            <Button variant="link" className="p-0 align-baseline small" onClick={fetchCount}>Thử lại</Button>
          </span>
        ) : (
          <Button
            variant="link"
            className="p-0 align-baseline small text-decoration-underline"
            title="Xem danh sách đơn đã đặt nhưng chưa hoàn tất nhập kho"
            onClick={() => setShowList(true)}
          >
            {count} đơn hàng đang chờ nhập kho
          </Button>
        )}
        {/* #230341 — cảnh báo đỏ phải thấy được TRƯỚC khi bấm mở bảng: nếu phải mở modal mới biết
            có đơn quá hạn thì Dashboard vẫn không nói được điều sếp cần biết trong một cái liếc. */}
        {!loading && !error && overdue > 0 && (
          <span
            className="text-danger fw-bold"
            title="Đơn đã quá 24h chờ NV xác nhận nhập kho — bấm vào dòng để xem chi tiết"
          >
            🚨 {overdue} quá hạn
          </span>
        )}
      </div>

      <PendingReceiptsModal
        show={showList}
        onHide={() => setShowList(false)}
        branchId={branchId}
        branchName={selectedBranch?.name}
      />
    </>
  );
};

export default PendingReceiptsLine;
